import { useCallback, useEffect, useRef, useState } from 'react';

import { CheckCfTokenFailure, useCheckCfTokenMutation } from '../api/cloudflareTokenApi';
import { CfTokenCheckState } from './cfTokenFieldStatus';

export const CHECK_DEBOUNCE_MS = 500;

type CheckFor = { token: string; state: CfTokenCheckState; betaClosed: boolean };

const IDLE: CfTokenCheckState = { phase: 'idle' };

export type CfTokenCheck = {
  checkState: CfTokenCheckState;
  betaClosed: boolean;
  rejectedChecks: number;
  retry: () => void;
};

const answerTo = (token: string, failure: Partial<CheckCfTokenFailure> | undefined): CheckFor => {
  switch (failure?.reason) {
    case 'beta_closed':
      return { token, state: IDLE, betaClosed: true };
    case 'invalid':
      return { token, state: { phase: 'answered', result: 'invalid', coversOtherZones: false }, betaClosed: false };
    default:
      return { token, state: { phase: 'unchecked' }, betaClosed: false };
  }
};

const isRejection = ({ state }: CheckFor): boolean =>
  state.phase === 'answered' && state.result !== 'valid' && state.result !== 'unavailable';

export const useCfTokenCheck = (domain: string, tokenValue: string): CfTokenCheck => {
  const [checkToken, { reset }] = useCheckCfTokenMutation();
  // RTK Query hands out a new `reset` after every request; as an effect dependency
  // it re-arms the debounce each time a check answers, and the check loops.
  const forgetCheckRequest = useRef(reset);
  forgetCheckRequest.current = reset;
  const [latest, setLatest] = useState<CheckFor | null>(null);
  const [rejectedChecks, setRejectedChecks] = useState(0);
  const awaitedToken = useRef<string | null>(null);
  const trimmed = tokenValue.trim();

  const check = useCallback(
    async (token: string) => {
      awaitedToken.current = token;
      setLatest({ token, state: { phase: 'checking' }, betaClosed: false });

      let answer: CheckFor;
      try {
        const { result, coversOtherZones } = await checkToken({ domain, token }).unwrap();
        answer = { token, state: { phase: 'answered', result, coversOtherZones }, betaClosed: false };
      } catch (error) {
        answer = answerTo(token, error as Partial<CheckCfTokenFailure> | undefined);
      }

      if (awaitedToken.current !== token) return;

      setLatest(answer);
      if (isRejection(answer)) setRejectedChecks((count) => count + 1);
    },
    [checkToken, domain]
  );

  useEffect(() => {
    if (trimmed === '') {
      awaitedToken.current = null;
      setLatest(null);
      forgetCheckRequest.current();
      return undefined;
    }

    const timer = setTimeout(() => check(trimmed), CHECK_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [check, trimmed]);

  const current = trimmed !== '' && latest?.token === trimmed ? latest : null;

  return {
    checkState: current?.state ?? IDLE,
    betaClosed: current?.betaClosed ?? false,
    rejectedChecks,
    retry: () => {
      if (trimmed !== '') check(trimmed);
    },
  };
};
